import { cosignErrorCode, LOGIN_METHOD } from '@constants'
import { setLoginOrSignUpMethod } from '@dni-analysis/events/sendLoginEvent'
import { Alert } from '@pets-ui/Alert/Alert'
import { ArrowLeftSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import * as stylex from '@stylexjs/stylex'
import { type AxiosError } from 'axios'
import { format } from 'date-fns'
import queryString from 'query-string'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOtpCosignSend } from '../../../../hooks/useOtpCosignSend'
import { useTimer } from '../../../../hooks/useTimer'
import { type SendOtpErrorResponse } from '../../../../types/apiErrorResponse'
import { getAccount } from '../../../../utils/cookie'
import { LoadingProvider } from '../../../LoadingProvider'
import { LoginLink } from '../../LoginLink'
import { Service } from '../../Service'
import { CardTitleWithIcon } from '../CardTitleWithIcon'
import { DividerOr } from '../DividerOr'
import { InputVerification } from '../InputVerification'
import { useContextLoginGroupBuying } from './useContextLoginGroupBuying'

const styles = stylex.create({
  subTitleContainer: {
    gap: '4px',
    alignItems: 'center',
    color: '#4B4B4B',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: '20px',
    marginTop: '20px',
  },
  validateCodeContainer: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  resendContainer: {
    gap: '4px',
    display: 'flex',
  },
  validateCodeTitle: {
    color: '#4B4B4B',
  },
  validateResendText: {
    color: '#AAAAAA',
  },
})

export function ValidationEmailGroupBuying() {
  const { t } = useTranslation()
  const { email, backStep, tokenLoginUrl, redirectUrl, fallbackUrl } = useContextLoginGroupBuying()

  const { startTimer, seconds, isCounting } = useTimer()
  const [remainingTimes, setRemainingTimes] = useState(0)
  const [inputVerificationError, setInputVerificationError] = useState('')
  const { bindSendOtp, isMutating } = useOtpCosignSend({ tokenLoginUrl, redirectUrl, fallbackUrl })
  const search = queryString.parse(window.location.search)
  const account = email || getAccount()
  const canSendOtp = useRef(!search?.otpCode)
  const handleSendOtp = useCallback(async () => {
    try {
      canSendOtp.current = false
      const { data } = await bindSendOtp('email')
      startTimer(data.expiredAt)
      setRemainingTimes(data.remainingTimes)
    }
    catch (error) {
      const { response } = error as AxiosError<SendOtpErrorResponse>
      if (response?.data?.code === cosignErrorCode.OTP_RESEND_LIMIT_REACHED || response?.data?.code === cosignErrorCode.OTP_RESEND_LIMIT_REACHED_2) {
        startTimer(response.data.detail.expiredAt)
        setRemainingTimes(response.data.detail.remainingTimes ?? 0)
      }
    }
  }, [bindSendOtp, startTimer])

  useEffect(function recordEmailMethodOnMount() {
    // 帶 otpCode 進站時 handleSendOtp 不會執行（canSendOtp 為 false），但 magic-link 仍會自動驗證登入，method 須在此獨立補記
    setLoginOrSignUpMethod(LOGIN_METHOD.EMAIL)
  }, [])

  useEffect(() => {
    if (!canSendOtp.current) return
    handleSendOtp()
  }, [handleSendOtp])

  const handleResendOtp = useCallback(() => {
    handleSendOtp()
    setInputVerificationError('')
  }, [handleSendOtp])

  const handleClickBack = () => {
    backStep()
  }

  const shouldShowRemainingTimesAlert = remainingTimes != null && seconds !== 0 && (remainingTimes >= 0 && remainingTimes < 5)
  const shouldShowResendText = remainingTimes !== 0 || seconds === 0
  const displayRemainingTimes = format(seconds * 1000, 'mm:ss')
  return (
    <LoadingProvider loading={isMutating}>
      <CardTitleWithIcon
        title={t('login.verifyIdentity')}
        leftIcon={<ArrowLeftSmall />}
        onClickLeft={handleClickBack}
      />
      <div {...stylex.props(styles.subTitleContainer)}>
        <Text variant="body">{t('login.sendVerifyCodeTo')}</Text>
        <Text variant="h5">{account}</Text>
      </div>
      <div {...stylex.props(styles.validateCodeContainer)}>
        <Text variant="caption" rootStyle={styles.validateCodeTitle}>{t('login.sendEmailTitle')}</Text>
        <InputVerification error={inputVerificationError} setError={setInputVerificationError} purpose="bind" />
        {shouldShowResendText && (
          <div {...stylex.props(styles.resendContainer)}>
            <Text variant="caption" rootStyle={styles.validateResendText}>
              {t('login.didntReceiveVerificationCode')}
              {seconds > 0 ? `(${seconds}s)` : ''}
            </Text>
            <LoginLink disabled={isCounting} onClick={handleResendOtp}>{t('login.resend')}</LoginLink>
          </div>
        )}
        {shouldShowRemainingTimesAlert
        && (
          <Alert type="warning">
            <Text variant="caption">
              {t('login.resendErmainingTimesContent', { remainingTimes })}
              {remainingTimes === 0 && `${t('login.resendWaitContent')}(${displayRemainingTimes})`}
            </Text>
          </Alert>
        )}
      </div>
      <DividerOr />
      <Service />
    </LoadingProvider>
  )
}
